/**
 * Sidebar content for the "2 - Strategy" step.
 * Shows a compact assembly list of selected devices grouped by catalog.
 */

import { AssemblyList } from './assembly-list';

import type { IntroductionSide } from '@/workflows/vascular/common/catalog';
import { MeasurementSection } from '@/workflows/vascular/common/components/measurements';
import { SegmentedToggle } from '@/workflows/vascular/common/components/segmented-toggle';
import { useStrategyState } from '@/workflows/vascular/evar/hooks/use-strategy-state';
import { EVAR_CATALOG_DESCRIPTORS } from '@/workflows/vascular/evar/strategy';

export function EvarStrategyPanel() {
    const strategy = useStrategyState();

    return (
        <div className="flex flex-col h-full gap-1.5 overflow-y-auto">
            <MeasurementSection title="Interventional Strategy">
                {strategy && (
                    <IntroductionSideToggle
                        value={strategy.introductionSide}
                        onChange={strategy.setIntroductionSide}
                    />
                )}
            </MeasurementSection>

            <MeasurementSection title="Selected Devices">
                {/* Introduction side toggle */}

                {strategy ? (
                    <AssemblyList
                        assembly={strategy.assembly}
                        onRemove={strategy.removePart}
                        onClearCatalog={strategy.clearAssembly}
                        compact
                        catalogDescriptors={EVAR_CATALOG_DESCRIPTORS}
                    />
                ) : (
                    <p className="px-2 py-1 text-xs text-muted-foreground">Loading...</p>
                )}
            </MeasurementSection>

            {strategy && strategy.assembly.length > 0 && (
                <div className="px-2 py-2 mt-auto border-t border-border">
                    <p className="text-[10px] text-muted-foreground">
                        {strategy.assembly.length} device{strategy.assembly.length !== 1 ? 's' : ''} selected
                    </p>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Introduction Side Toggle
// ============================================================================

const SIDE_OPTIONS = [
    { value: 'left' as const, label: 'Left' },
    { value: 'right' as const, label: 'Right' },
];

function IntroductionSideToggle({
    value,
    onChange,
}: {
    value: IntroductionSide;
    onChange: (side: IntroductionSide) => void;
}) {
    return (
        <div className="flex items-center gap-2 px-3 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Introduction Side
            </span>
            <SegmentedToggle options={SIDE_OPTIONS} value={value} onChange={onChange} />
        </div>
    );
}
